import { Helmet } from "react-helmet-async";
import { useSiteContext } from "@/contexts/SiteContext";
import { isNonIndexablePath } from "@/utils/seoPathGuards";

interface DefaultSEOProps {
  pagePath?: string;
}

// Page-specific default meta data for SEO fallback
const PAGE_DEFAULTS: Record<string, { title: string; description: string }> = {
  '/': {
    title: 'Pharma Trial Connect — Clinical Trial Matching',
    description: 'AI-powered clinical trial matching across Africa and Asia. 193K+ trials, free for patients, BRIDGE™ infrastructure for sites.'
  },
  '/patients': {
    title: 'Clinical Trial Matching for Patients | Pharma Trial Connect',
    description: 'Find clinical trials with AI-powered matching. Free for patients and caregivers. Access 193K+ trials across 120+ countries.'
  },
  '/sponsors': {
    title: 'Trial Solutions for Sponsors | Pharma Trial Connect',
    description: 'Accelerate patient recruitment with AI-powered matching. Access diverse populations across Africa and globally. Book a demo today.'
  },
  '/trial-sites': {
    title: 'Clinical Trial Site Solutions & BRIDGE™ Platform | Pharma Trial Connect',
    description: 'Transform your site into sponsor-ready infrastructure in 90 days. BRIDGE™ middleware, offline-first technology, and complete trial management.'
  },
  '/bridge': {
    title: 'BRIDGE™ - Last Mile Clinical Trial Infrastructure | Pharma Trial Connect',
    description: 'BRIDGE™ connects global pharmaceutical innovation with underserved communities. Digitize trial sites in 90 days with offline-first technology.'
  },
  '/health-passport-africa': {
    title: 'African Health Passport™ - Patient Data Ownership | Pharma Trial Connect',
    description: 'Own your health data with African Health Passport™. WhatsApp-based access, data sovereignty, and seamless trial participation.'
  },
  '/conditions': {
    title: 'Browse Clinical Trials by Medical Condition | Pharma Trial Connect',
    description: 'Find clinical trials for cancer, diabetes, heart disease, Alzheimer\'s, rare diseases and more. AI-matched to your health profile.'
  },
  '/regions': {
    title: 'Clinical Trials by Region & Country | Pharma Trial Connect',
    description: 'Find clinical trials near you or globally. Coverage across 54 African countries, US, Europe, Asia, and Latin America.'
  },
  '/about': {
    title: 'About Pharma Trial Connect - Bridging Global Trials to the Last Mile',
    description: 'PTC transforms clinical trial infrastructure across Africa and Asia through BRIDGE™ middleware and African Health Passport™. 193K+ trials, 54 countries.'
  },
  '/mission': {
    title: 'Our Mission & Vision | Pharma Trial Connect',
    description: 'Democratizing clinical trial access by building last-mile infrastructure. Our vision: trials as accessible in rural Africa as in Boston by 2030.'
  },
  '/contact': {
    title: 'Contact Pharma Trial Connect | Get in Touch',
    description: 'Have questions about clinical trials or our platform? Contact our team for personalized support and guidance.'
  },
  '/blog': {
    title: 'Clinical Trial Insights & Research Updates | Pharma Trial Connect Blog',
    description: 'Stay informed with expert insights, research updates, and guidance on clinical trial participation and medical innovation.'
  },
  '/resources': {
    title: 'Clinical Trial Resources & Tools | Pharma Trial Connect',
    description: 'Access guides, case studies, calculators, and educational resources for patients, sites, and sponsors.'
  },
  '/faq': {
    title: 'Frequently Asked Questions | Pharma Trial Connect',
    description: 'Get answers to common questions about clinical trials, participation, safety, and how our platform works.'
  },
  '/education': {
    title: 'Clinical Trial Education Center | Pharma Trial Connect',
    description: 'Learn about clinical trials, phases, safety, participation, and how to find the right trial for you.'
  },
  '/assessments': {
    title: 'Free Site Readiness Assessment | Pharma Trial Connect',
    description: 'Get a comprehensive site readiness evaluation worth $5K-$30K. Prove trial-readiness to sponsors and access new opportunities.'
  },
  '/demo-booking': {
    title: 'Book a Demo | Pharma Trial Connect',
    description: 'See how Pharma Trial Connect can transform your clinical trial operations. Schedule a personalized demo today.'
  },
  '/careers': {
    title: 'Careers at Pharma Trial Connect | Join Our Team',
    description: 'Join our mission to democratize clinical trial access globally. View open positions and become part of healthcare innovation.'
  },
  '/directory': {
    title: 'Clinical Trial Directory | Pharma Trial Connect',
    description: 'Browse research centers, advocacy groups, and trial sites. Comprehensive directory of clinical trial resources.'
  },
  '/directory/research-centers': {
    title: 'Global Research Centers Directory | Pharma Trial Connect',
    description: 'Find world-class research centers and clinical trial sites across Africa, US, Europe, and Asia-Pacific.'
  },
  '/directory/advocacy': {
    title: 'Patient Advocacy Groups Directory | Pharma Trial Connect',
    description: 'Connect with patient advocacy organizations by condition and region. Support networks for trial participants.'
  },
  '/offline-first': {
    title: 'Offline-First Clinical Trial Technology | Pharma Trial Connect',
    description: 'Conduct clinical trials without reliable internet. Offline-first technology syncs automatically when connectivity returns.'
  },
  '/decentralized-trials': {
    title: 'Decentralized Clinical Trials (DCT) Solutions | Pharma Trial Connect',
    description: 'Enable hybrid and fully decentralized clinical trials. Remote monitoring, eConsent, and patient-centric trial design.'
  },
  '/cro-services': {
    title: 'CRO Services & Solutions | Pharma Trial Connect',
    description: 'Operational excellence for CROs. Multi-site coordination, quality compliance, and sponsor reporting solutions.'
  },
  '/smo-services': {
    title: 'Site Management Organization (SMO) Services | Pharma Trial Connect',
    description: 'Comprehensive site management support. Regulatory assistance, staff training, and operational optimization.'
  },
  '/datasets': {
    title: 'Clinical Trial Datasets & Analytics | Pharma Trial Connect',
    description: 'Access curated clinical trial datasets, advanced analytics, and data export capabilities for research.'
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Pharma Trial Connect',
    description: 'Learn how Pharma Trial Connect protects your personal and health information. HIPAA, GDPR, and POPIA compliant.'
  },
  '/terms-of-service': {
    title: 'Terms of Service | Pharma Trial Connect',
    description: 'Review the terms and conditions for using Pharma Trial Connect platform and services.'
  },
  '/hipaa-notice': {
    title: 'HIPAA Notice of Privacy Practices | Pharma Trial Connect',
    description: 'Important information about how we handle protected health information in compliance with HIPAA regulations.'
  },
  '/accessibility': {
    title: 'Accessibility Statement | Pharma Trial Connect',
    description: 'Our commitment to making clinical trial information accessible to all users regardless of ability.'
  },
  '/diversity-inclusion': {
    title: 'Diversity & Inclusion in Clinical Trials | Pharma Trial Connect',
    description: 'Our commitment to increasing diversity in clinical trials and ensuring equitable access to medical research.'
  }
};

export function DefaultSEO({ pagePath }: DefaultSEOProps) {
  const { site } = useSiteContext();
  const path = pagePath || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const defaults = PAGE_DEFAULTS[path] || {
    title: 'Pharma Trial Connect - Clinical Trial Matching Platform',
    description: 'Connect with clinical trials globally. AI-powered matching for patients, comprehensive solutions for sites and sponsors.'
  };

  // White-label aware: custom domains canonicalize to themselves; everything else
  // (apex, www, lovable.app preview, lovable subdomains) canonicalizes to pharmatrialconnect.com.
  const currentHostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isCustomDomain = site && !currentHostname.includes('pharmatrialconnect') && !currentHostname.includes('lovable.app');
  const canonicalDomain = isCustomDomain
    ? `https://${currentHostname}`
    : 'https://pharmatrialconnect.com';
  const canonicalUrl = `${canonicalDomain}${path}`;

  // Guard internal/auth/admin/transactional routes — never index, never pass link equity.
  const shouldNoIndex = isNonIndexablePath(path);
  const robotsContent = shouldNoIndex
    ? 'noindex, nofollow'
    : 'index, follow';

  return (
    <Helmet>
      <title>{defaults.title}</title>
      <meta name="description" content={defaults.description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang alternates live in index.html (static, server-rendered) so crawlers
          see them without JS. Do not duplicate them here — <link> tags don't dedupe by rel. */}

      {/* Open Graph */}
      <meta property="og:title" content={defaults.title} />
      <meta property="og:description" content={defaults.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content="https://pharmatrialconnect.com/lovable-uploads/1287e66a-7a3e-48a7-ac45-380a5079191a.png" />
      <meta property="og:site_name" content="Pharma Trial Connect" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={defaults.title} />
      <meta name="twitter:description" content={defaults.description} />
      <meta name="twitter:image" content="https://pharmatrialconnect.com/lovable-uploads/1287e66a-7a3e-48a7-ac45-380a5079191a.png" />

      {/* Additional SEO */}
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <meta name="language" content="English" />
    </Helmet>
  );
}

// Export page defaults for use in other components
export { PAGE_DEFAULTS };

export default DefaultSEO;
